import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, Heart, MessageCircle, MessageSquare, ShieldCheck, Check, Loader2, Repeat2, Flag, Radio, Sparkles, Megaphone, Pin, Flame, UserPlus, KeyRound, CalendarClock } from "lucide-react";
import api from "../lib/api";
import { Avatar } from "./Media";
import { useRealtime } from "../context/RealtimeContext";
import { useAuth } from "../context/AuthContext";
import { pushSupported, enablePush } from "../lib/push";

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const verb = { like: "admired your signal", comment: "left an exchange", reply: "replied to you", reshare: "reposted your signal", approved: "Welcome - you've been admitted", disturbance_ack: "Disturbance reported to the Council, thank you, we're reviewing it.", meet_live: "is live in the encrypted meet-up", resonance: "A sovereign you brought in took root: +1 Resonance Point", hearth_announce: "New announcement in", hearth_pin: "A post was pinned in", hearth_post: "New post in", hearth_event: "Live session:", hearth_comment: "commented on your post in", hearth_thread: "replied in a conversation you're in:", hearth_invite: "invited you to their private community", beacon_comment: "commented on a Beacon lesson:", beacon_waiting: "Someone is waiting for access to your Beacon: tap to let them in", beacon_milestone: "", council_reply: "", phrase_checkin: "For peace of mind, take a moment to check that your recovery phrase still works." };
const Icon = ({ type }) => {
  if (type === "like") return <Heart size={14} className="text-[#14A89A]" />;
  if (type === "comment") return <MessageCircle size={14} className="text-[#14A89A]" />;
  if (type === "reply") return <MessageCircle size={14} className="text-[#14A89A]" />;
  if (type === "reshare") return <Repeat2 size={14} className="text-[#14A89A]" />;
  if (type === "disturbance_ack") return <Flag size={14} className="text-[#A855F7]" />;
  if (type === "meet_live") return <Radio size={14} className="text-[#14A89A]" />;
  if (type === "resonance") return <Sparkles size={14} className="text-[#14A89A]" />;
  if (type === "hearth_announce") return <Megaphone size={14} className="text-[#14A89A]" />;
  if (type === "hearth_post") return <MessageSquare size={14} className="text-[#14A89A]" />;
  if (type === "hearth_comment" || type === "hearth_thread" || type === "beacon_comment") return <MessageCircle size={14} className="text-[#14A89A]" />;
  if (type === "hearth_event") return <CalendarClock size={14} className="text-[#14A89A]" />;
  if (type === "hearth_pin") return <Pin size={14} className="text-[#14A89A]" />;
  if (type === "hearth_invite") return <UserPlus size={14} className="text-[#14A89A]" />;
  if (type === "beacon_waiting") return <Flame size={14} className="text-[#14A89A]" />;
  return <Check size={14} className="text-[#14A89A]" />;
};

export const NotificationBell = ({ mobile }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef();
  const dropdownRef = useRef();
  const navigate = useNavigate();
  const { event } = useRealtime();
  const { user } = useAuth();
  const [notifPerm, setNotifPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const [enabling, setEnabling] = useState(false);

  const enableAlerts = async () => {
    setEnabling(true);
    try {
      await enablePush();
    } catch (e) { /* noop */ }
    setNotifPerm(typeof Notification !== "undefined" ? Notification.permission : "denied");
    setEnabling(false);
  };
  const showAlertsCta = pushSupported() && notifPerm === "default";

  const poll = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.count);
    } catch {}
  };

  useEffect(() => {
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, []);

  // instant bump on realtime notification
  useEffect(() => {
    if (event && event.type === "notification") poll();
  }, [event]);

  // position the portalled dropdown under the bell button
  const positionDropdown = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 24);
    // anchor right edge to the bell on mobile, left edge on desktop
    let left = mobile ? r.right - width : r.left;
    left = Math.max(12, Math.min(left, window.innerWidth - width - 12));
    setPos({ top: r.bottom + 8, left });
  };

  useLayoutEffect(() => {
    if (!open) return;
    positionDropdown();
    window.addEventListener("resize", positionDropdown);
    window.addEventListener("scroll", positionDropdown, true);
    return () => {
      window.removeEventListener("resize", positionDropdown);
      window.removeEventListener("scroll", positionDropdown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const onClick = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(true);
      try {
        const { data } = await api.get("/notifications");
        setItems(data);
        if (unread > 0) { await api.post("/notifications/read"); setUnread(0); }
      } catch {} finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="relative">
      <button ref={btnRef} onClick={toggle} data-testid="notification-bell" className="relative transition-colors">
        <Bell strokeWidth={1.5} size={mobile ? 22 : 18} className="text-[#D4AF37] hover:text-[#F3E5AB]" />
        {unread > 0 && (
          <span data-testid="notification-badge" className="absolute -top-1.5 -right-1.5 bg-[#14A89A] text-[#0A0A0A] text-[9px] font-bold min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={dropdownRef}
          data-testid="notification-dropdown"
          style={{ position: "fixed", top: pos.top, left: pos.left, width: "min(20rem, calc(100vw - 1.5rem))", zIndex: 2147483000 }}
          className="max-h-[70vh] overflow-y-auto bg-[#121212] border border-white/10 shadow-2xl shadow-black/60"
        >
          <div className="px-4 py-3 border-b border-white/10 font-body text-[10px] tracking-[0.2em] uppercase text-zinc-500">Signals</div>
          {showAlertsCta && (
            <button onClick={enableAlerts} disabled={enabling} data-testid="notif-enable-alerts" className="w-full flex items-center gap-3 px-4 py-3 bg-[#14A89A]/8 hover:bg-[#14A89A]/15 transition-colors text-left border-b border-white/10 disabled:opacity-60">
              {enabling ? <Loader2 size={16} className="animate-spin text-[#14A89A] shrink-0" /> : <BellRing size={16} className="text-[#14A89A] shrink-0" />}
              <div className="min-w-0">
                <p className="font-body text-xs text-white leading-snug">Turn on alerts</p>
                <p className="font-body text-[10px] text-zinc-400 leading-snug mt-0.5">Be notified when a sovereign friend reaches out.</p>
              </div>
            </button>
          )}
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="animate-spin text-zinc-500" size={18} /></div>
          ) : items.length === 0 ? (
            <p className="font-body text-sm text-zinc-600 p-5">Nothing yet.</p>
          ) : items.map((n) => (
            <button key={n.id} onClick={() => { if (n.type === "phrase_checkin") navigate(`/profile/${user?.id}?checkphrase=1`); else if (n.type === "beacon_waiting" || n.type === "beacon_milestone") navigate("/beacon"); else if (n.type === "beacon_comment") navigate(`/beacon/view/${n.post_id}`); else if (n.type === "council_reply") navigate("/council/messages"); else if (n.type === "resonance") navigate(`/profile/${user?.id}`); else if (n.type === "meet_live") navigate("/meetup"); else if (n.type === "hearth_invite") navigate("/hearths"); else if (n.type === "hearth_announce" || n.type === "hearth_pin" || n.type === "hearth_post" || n.type === "hearth_comment" || n.type === "hearth_thread" || n.type === "hearth_event") navigate(`/hearth/${n.post_id}`); else if (n.post_id) navigate(`/post/${n.post_id}`); else if (n.actor_id) navigate(`/profile/${n.actor_id}`); setOpen(false); }} data-testid="notification-item" className="w-full flex items-start gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left border-b border-white/5">
              {n.type === "resonance"
                ? <div className="w-[34px] h-[34px] flex items-center justify-center bg-[#14A89A]/10 shrink-0"><Sparkles size={16} className="text-[#14A89A]" /></div>
                : n.type === "beacon_waiting"
                ? <div className="w-[34px] h-[34px] flex items-center justify-center bg-[#14A89A]/10 shrink-0"><Flame size={16} className="text-[#14A89A]" /></div>
                : n.type === "council_reply"
                ? <div className="w-[34px] h-[34px] flex items-center justify-center bg-[#14A89A]/10 shrink-0"><ShieldCheck size={16} className="text-[#14A89A]" /></div>
                : n.type === "beacon_milestone"
                ? <div className="w-[34px] h-[34px] flex items-center justify-center bg-[#14A89A]/10 shrink-0"><Sparkles size={16} className="text-[#D4AF37]" /></div>
                : n.type === "phrase_checkin"
                ? <div className="w-[34px] h-[34px] flex items-center justify-center bg-[#14A89A]/10 shrink-0"><KeyRound size={16} className="text-[#14A89A]" /></div>
                : <Avatar name={n.actor_name} src={n.actor_avatar} size={34} />}
              <div className="min-w-0 flex-1">
                <p className="font-body text-xs text-zinc-200 leading-snug">
                  {n.type !== "approved" && n.type !== "disturbance_ack" && n.type !== "resonance" && n.type !== "hearth_announce" && n.type !== "hearth_pin" && n.type !== "hearth_post" && n.type !== "hearth_event" && n.type !== "beacon_waiting" && n.type !== "phrase_checkin" && n.type !== "beacon_milestone" && n.type !== "council_reply" && <span className="text-white font-medium">{n.actor_name} </span>}
                  {n.type === "council_reply" && <span className="text-white font-medium">{n.text}</span>}
                  {n.type === "beacon_milestone" && <span className="text-white font-medium">{n.text}</span>}
                  {verb[n.type] || "interacted"}
                  {(n.type === "hearth_announce" || n.type === "hearth_pin" || n.type === "hearth_post" || n.type === "hearth_comment" || n.type === "hearth_thread" || n.type === "beacon_comment" || n.type === "hearth_event") && n.text && <span className="text-white font-medium"> {n.text}</span>}
                  {n.type === "hearth_invite" && n.text && <span className="text-white font-medium"> · {n.text}</span>}
                  {n.type === "comment" && n.text && <span className="text-zinc-500">: "{n.text.slice(0, 40)}"</span>}
                  {n.type === "reply" && n.text && <span className="text-zinc-500">: "{n.text.slice(0, 40)}"</span>}
                </p>
                {n.type === "disturbance_ack" && n.text && <p className="font-body text-[11px] text-zinc-500 mt-0.5 leading-snug">{n.text}</p>}
                {n.type === "meet_live" && n.text && <p className="font-body text-[11px] text-zinc-500 mt-0.5 leading-snug">"{n.text.slice(0, 60)}"</p>}
                <span className="font-body text-[10px] text-zinc-600">{timeAgo(n.created_at)}</span>
              </div>
              <Icon type={n.type} />
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};
